<script setup>
import { usePlatformApi } from '@/utils/platformApi'

// Addendum 15, Modul BQ. Sama pola auth-check & dua lapis keamanan dengan
// halaman /platform lain -- lihat komentar di platform/settings/index.vue.
definePage({ meta: { layout: 'platform', public: true } })

const router = useRouter()

const platformAccessToken = useCookie('platformAccessToken')
const platformAdminData = useCookie('platformAdminData')

if (!platformAccessToken.value)
  await router.push('/platform/login')

const pages = ref([])
const rowState = ref({})
const fetchLoading = ref(true)
const fetchError = ref('')

const handleAuthError = async err => {
  const status = err?.status ?? err?.response?.status
  if (status === 401 || status === 403) {
    platformAccessToken.value = null
    platformAdminData.value = null
    await router.push('/platform/login')

    return true
  }

  return false
}

// Addendum 15, Modul BT: og_image_url (URL siap-pakai buat preview <img>)
// ditambahkan berdampingan dengan og_image_path (nilai mentah yang dikirim
// balik saat simpan) -- og_image_path bisa berupa URL manual ATAU path
// hasil upload, og_image_url SELALU URL absolut yang benar untuk ditampilkan
// (lihat SeoSettingService::resolveImageUrl()).
const buildRowState = page => ({
  form: {
    route_path: page.route_path,
    meta_title: page.meta_title ?? '',
    meta_description: page.meta_description ?? '',
    meta_keywords: page.meta_keywords ?? '',
    og_image_path: page.og_image_path ?? '',
  },
  imagePreviewUrl: page.og_image_url ?? null,
  uploadingImage: false,
  imageError: '',
  loading: false,
  errors: {},
  successMessage: '',
})

const fetchPages = async () => {
  fetchLoading.value = true
  fetchError.value = ''
  try {
    const res = await usePlatformApi('/api/v1/platform/seo-settings').get()
    const body = res?.data ?? res

    pages.value = body?.data ?? body ?? []

    const state = {}
    for (const page of pages.value)
      state[page.route_path] = buildRowState(page)
    rowState.value = state
  } catch (err) {
    if (await handleAuthError(err))
      return

    fetchError.value = 'Gagal memuat daftar halaman publik.'
    console.error('[Platform] fetch seo-settings error', err)
  } finally {
    fetchLoading.value = false
  }
}

onMounted(fetchPages)

const save = async routePath => {
  const state = rowState.value[routePath]

  state.loading = true
  state.errors = {}
  state.successMessage = ''

  try {
    const res = await usePlatformApi('/api/v1/platform/seo-settings').put({ body: state.form })
    const body = res?.data ?? res

    state.successMessage = body?.message ?? 'Pengaturan SEO halaman berhasil disimpan.'
  } catch (err) {
    if (await handleAuthError(err))
      return

    const status = err?.status ?? err?.response?.status
    const data = err?.data ?? err?.response?._data

    if (status === 422 && data?.errors)
      state.errors = data.errors
    else
      state.errors = { _general: [data?.message ?? 'Gagal menyimpan pengaturan SEO.'] }
  } finally {
    state.loading = false
  }
}

// Addendum 15, Modul BT: reuse pola upload ProductController/BlogPostController
// -- FormData dikirim lewat usePlatformApi (sudah otomatis menangani header
// multipart & Authorization platform, lihat utils/platformApi.js), TIDAK
// perlu komponen upload baru. Upload langsung tersimpan begitu file dipilih
// (tidak perlu klik "Simpan" terpisah) supaya konsisten dengan preview yang
// langsung berubah.
const uploadImage = async (routePath, file) => {
  if (!file) return

  const state = rowState.value[routePath]

  state.uploadingImage = true
  state.imageError = ''

  const fd = new FormData()

  fd.append('route_path', routePath)
  fd.append('image', file)

  try {
    const res = await usePlatformApi('/api/v1/platform/seo-settings/image').post({ body: fd })
    const setting = res?.data ?? res

    state.form.og_image_path = setting?.og_image_path ?? state.form.og_image_path
    state.imagePreviewUrl = setting?.og_image_url ?? state.imagePreviewUrl
    state.successMessage = res?.message ?? 'Gambar berhasil diupload.'
  } catch (err) {
    if (await handleAuthError(err))
      return

    const data = err?.data ?? err?.response?._data

    state.imageError = data?.message ?? 'Gagal mengupload gambar.'
  } finally {
    state.uploadingImage = false
  }
}
</script>

<template>
  <div class="pa-6">
    <div class="d-flex align-center justify-space-between mb-4 flex-wrap gap-3">
      <div>
        <h4 class="text-h4">
          SEO Halaman Publik
        </h4>
        <p class="text-body-2 text-medium-emphasis mb-0">
          Login sebagai {{ platformAdminData?.name }}
        </p>
      </div>
    </div>

    <VCard max-width="900">
      <VCardTitle class="pt-5 px-5">
        Judul, Deskripsi & Open Graph per Halaman
      </VCardTitle>
      <VCardSubtitle class="px-5 pb-2">
        Mengatur tag <code>&lt;title&gt;</code>, meta description, keywords, dan gambar Open Graph
        (preview link WhatsApp/Facebook) untuk tiap halaman publik. Halaman yang belum diatur di sini
        tetap punya judul & deskripsi default yang wajar.
      </VCardSubtitle>

      <VDivider />

      <!-- Loading state -->
      <VCardText
        v-if="fetchLoading"
        class="pa-10 text-center"
      >
        <VProgressCircular
          indeterminate
          color="primary"
        />
      </VCardText>

      <!-- Fetch error -->
      <VCardText
        v-else-if="fetchError"
        class="pa-5"
      >
        <VAlert
          type="error"
          variant="tonal"
        >
          {{ fetchError }}
        </VAlert>
      </VCardText>

      <VCardText
        v-else
        class="pa-0"
      >
        <VExpansionPanels variant="accordion">
          <VExpansionPanel
            v-for="page in pages"
            :key="page.route_path"
          >
            <VExpansionPanelTitle>
              <div class="d-flex align-center gap-3">
                <span class="font-weight-medium">{{ page.label }}</span>
                <VChip
                  size="x-small"
                  variant="tonal"
                  color="secondary"
                >
                  {{ page.route_path }}
                </VChip>
                <VChip
                  v-if="!rowState[page.route_path]?.form.meta_title"
                  size="x-small"
                  variant="tonal"
                  color="warning"
                >
                  Pakai default
                </VChip>
              </div>
            </VExpansionPanelTitle>

            <VExpansionPanelText>
              <VAlert
                v-if="rowState[page.route_path]?.successMessage"
                type="success"
                variant="tonal"
                class="mb-4"
                closable
                @click:close="rowState[page.route_path].successMessage = ''"
              >
                {{ rowState[page.route_path].successMessage }}
              </VAlert>

              <VAlert
                v-if="rowState[page.route_path]?.errors._general"
                type="error"
                variant="tonal"
                class="mb-4"
              >
                {{ rowState[page.route_path].errors._general[0] }}
              </VAlert>

              <VRow>
                <VCol cols="12">
                  <VTextField
                    v-model="rowState[page.route_path].form.meta_title"
                    label="Judul (title tag)"
                    :placeholder="page.default_title"
                    counter="70"
                    maxlength="70"
                    :error-messages="rowState[page.route_path]?.errors.meta_title"
                    :hint="`Kosongkan untuk pakai default: &quot;${page.default_title}&quot;`"
                    persistent-hint
                  />
                </VCol>

                <VCol cols="12">
                  <VTextarea
                    v-model="rowState[page.route_path].form.meta_description"
                    label="Meta Description"
                    :placeholder="page.default_description"
                    rows="2"
                    counter="160"
                    maxlength="160"
                    :error-messages="rowState[page.route_path]?.errors.meta_description"
                    hint="Muncul di bawah judul pada hasil pencarian Google."
                    persistent-hint
                  />
                </VCol>

                <VCol
                  cols="12"
                  md="6"
                >
                  <VTextField
                    v-model="rowState[page.route_path].form.meta_keywords"
                    label="Meta Keywords (opsional)"
                    placeholder="mis. pos kasir, kasir coffee shop, aplikasi umkm"
                    :error-messages="rowState[page.route_path]?.errors.meta_keywords"
                  />
                </VCol>

                <VCol
                  cols="12"
                  md="6"
                >
                  <VTextField
                    v-model="rowState[page.route_path].form.og_image_path"
                    label="URL Gambar Open Graph (opsional)"
                    placeholder="https://..."
                    hint="Gambar yang tampil saat link halaman ini dibagikan ke WhatsApp/Facebook. Bisa diisi URL manual, atau upload file di sebelah."
                    persistent-hint
                    :error-messages="rowState[page.route_path]?.errors.og_image_path"
                  />
                </VCol>

                <VCol
                  cols="12"
                  md="6"
                >
                  <div class="d-flex align-center gap-3">
                    <VAvatar
                      v-if="rowState[page.route_path]?.imagePreviewUrl"
                      :image="rowState[page.route_path].imagePreviewUrl"
                      size="56"
                      rounded="lg"
                    />
                    <div
                      v-else
                      class="og-image-placeholder d-flex align-center justify-center flex-shrink-0"
                    >
                      <VIcon icon="ri-image-line" size="22" color="medium-emphasis" />
                    </div>

                    <VFileInput
                      label="Upload Gambar"
                      accept="image/*"
                      hide-details
                      density="compact"
                      prepend-icon=""
                      prepend-inner-icon="ri-upload-line"
                      variant="outlined"
                      :loading="rowState[page.route_path]?.uploadingImage"
                      @update:model-value="file => uploadImage(page.route_path, file)"
                    />
                  </div>
                  <p
                    v-if="rowState[page.route_path]?.imageError"
                    class="text-error text-caption mt-1 mb-0"
                  >
                    {{ rowState[page.route_path].imageError }}
                  </p>
                  <p
                    v-else-if="['/tentang-kami', '/kontak'].includes(page.route_path)"
                    class="text-caption text-medium-emphasis mt-1 mb-0"
                  >
                    Gambar ini juga tampil sebagai ilustrasi di bagian atas halaman publiknya.
                  </p>
                  <p class="text-caption text-medium-emphasis mt-1 mb-0">
                    Maks. 2MB · JPG, PNG
                  </p>
                </VCol>
              </VRow>
            </VExpansionPanelText>

            <VDivider />

            <VCardActions class="pa-4">
              <VSpacer />
              <VBtn
                color="primary"
                :loading="rowState[page.route_path]?.loading"
                @click="save(page.route_path)"
              >
                Simpan
              </VBtn>
            </VCardActions>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCardText>
    </VCard>
  </div>
</template>

<style scoped>
.og-image-placeholder {
  inline-size: 56px;
  block-size: 56px;
  border-radius: 8px;
  background: rgba(var(--v-theme-on-surface), 0.06);
}
</style>
